import { useMemo, useState } from 'react';
import type {
  LaborImportOptions,
  LaborImportPreview,
} from '@obraplan/core/application/contracts/purchase-lines';
import type { LaborRowData } from '@obraplan/core/domain/schedule/purchase-template/index';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { STATUS_TONE, VISIBLE_LINES, listed, type StatusFilter } from './conferencia-model';
import { Option, PreviewStatus, ReaderIssues, StatusFilters, Warnings } from './conferencia-parts';
import {
  LINE_STATUS_LABEL,
  countLaborByKind,
  countLines,
  formatHours,
  plural,
  type JoinedLine,
  type LoadedLabor,
} from './purchase-model';

export interface LaborConferenciaProps {
  readonly fileName: string;
  /** O que a leitura da aba "Mão de obra" produziu. */
  readonly labor: LoadedLabor;
  readonly preview: LaborImportPreview | undefined;
  readonly joined: readonly JoinedLine<LaborRowData>[] | undefined;
  readonly isPreviewing: boolean;
  readonly previewError: string | undefined;
  readonly options: LaborImportOptions;
  readonly onOptions: (next: LaborImportOptions) => void;
  /** Criar função é do cadastro da mão de obra (`employees.manage`). */
  readonly canCreateJobRoles: boolean;
}

/**
 * O passo de conferência da mão de obra: o HH de cada item do cronograma por item, aberto por
 * função do cadastro do cliente (ADR-171, RN-351).
 *
 * O que pode faltar é pouco — **funções** novas, que entram no cadastro só com a sua escolha,
 * e **itens** que o cronograma por item ainda não tem, cujas linhas ficam com erro até o item
 * ser importado. A soma das funções de um item que não bate com o HH dele é aviso, e a linha
 * entra assim mesmo.
 *
 * O **serviço de terceiros** (D9, 02/10/2026) entra com a descrição da função da composição e
 * o selo "terceiros": é contado à parte da mão de obra própria e nunca é função que falta.
 */
export function LaborConferencia(props: LaborConferenciaProps) {
  const { labor, preview, joined } = props;
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const counts = useMemo(() => (joined === undefined ? undefined : countLines(joined)), [joined]);
  const matching = useMemo(
    () =>
      joined === undefined
        ? []
        : filter === 'ALL'
          ? joined
          : joined.filter((line) => line.status === filter),
    [joined, filter],
  );

  const hours = useMemo(
    () => labor.rows.reduce((total, row) => total + row.hours, 0),
    [labor.rows],
  );
  const byKind = useMemo(() => countLaborByKind(labor.rows), [labor.rows]);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm">
        <strong>{props.fileName}</strong>{' '}
        <span className="text-(--color-content-muted)">
          · aba &ldquo;{labor.sheetName}&rdquo; → {plural(labor.rows.length, 'linha', 'linhas')} de
          HH · {formatHours(hours)} HH
        </span>
      </p>

      <p className="text-xs text-(--color-content-muted)">
        Própria: {plural(byKind.PROPRIA.lines, 'linha', 'linhas')} ·{' '}
        {formatHours(byKind.PROPRIA.hours)} HH. De terceiros:{' '}
        {plural(byKind.TERCEIROS.lines, 'linha', 'linhas')} · {formatHours(byKind.TERCEIROS.hours)}{' '}
        HH — o serviço de terceiros leva a descrição da função da composição e não entra no cadastro
        de funções.
      </p>

      {labor.unions > 0 && (
        <p className="text-xs text-(--color-content-muted)">
          {plural(labor.unions, 'linha se somou', 'linhas se somaram')} a outra do mesmo item e da
          mesma função — duas funções da composição do orçamento que viram a mesma do cadastro
          (RN-351).
        </p>
      )}

      <ReaderIssues
        id="conf-labor-issues"
        title="Linhas da planilha que não puderam ser lidas"
        issues={labor.issues}
      />

      {preview !== undefined && <Cadastro {...props} preview={preview} />}

      {preview !== undefined && (
        <Warnings
          id="conf-labor-warnings"
          title="A soma das funções não confere com o HH do item"
          warnings={preview.warnings}
        />
      )}

      {counts === undefined || joined === undefined ? (
        <PreviewStatus previewError={props.previewError} isPreviewing={props.isPreviewing} />
      ) : (
        <section aria-labelledby="conf-labor-table" className="flex flex-col gap-3">
          <h3 id="conf-labor-table" className="sr-only">
            Linhas de mão de obra da importação
          </h3>
          {props.previewError !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {props.previewError}
            </p>
          )}
          <StatusFilters
            filter={filter}
            onFilter={setFilter}
            counts={counts}
            total={joined.length}
          />

          {matching.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">Nenhuma linha neste filtro.</p>
          ) : (
            <TableWrapper className="max-h-72 overflow-y-auto">
              <Table className="min-w-[48rem]">
                <TableHeader sticky>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-14">Linha</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>Pavimento</TableHead>
                    <TableHead>Disciplina</TableHead>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Trecho</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead className="text-right">HH</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matching.slice(0, VISIBLE_LINES).map((line) => (
                    <TableRow key={line.index}>
                      <TableCell className="tabular text-(--color-content-muted)">
                        {line.row.rowNumber}
                      </TableCell>
                      <TableCell>
                        <Badge tone={STATUS_TONE[line.status]}>
                          {LINE_STATUS_LABEL[line.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-32 truncate">{line.row.floorName}</TableCell>
                      <TableCell className="max-w-36 truncate">{line.row.disciplineName}</TableCell>
                      <TableCell className="max-w-40 truncate">
                        {line.row.serviceName ?? (
                          <span className="italic text-(--color-content-muted)">
                            Frente inteira
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-28 truncate">{line.row.segment ?? '—'}</TableCell>
                      <TableCell className="max-w-48">
                        {line.row.kind === 'TERCEIROS' ? (
                          <span className="flex items-center gap-1.5">
                            <span className="truncate">{line.row.thirdPartyDescription}</span>
                            <Badge
                              tone="info"
                              title="Serviço de terceiros: não vira função do cadastro (D9)."
                            >
                              terceiros
                            </Badge>
                          </span>
                        ) : (
                          <span className="block truncate">{line.row.jobRoleName}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular">
                        {formatHours(line.row.hours)}
                      </TableCell>
                      <TableCell className="min-w-48 max-w-72 text-xs text-(--color-content-muted)">
                        {line.issues.map((issue) => issue.message).join(' ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
          {matching.length > VISIBLE_LINES && (
            <p className="text-xs text-(--color-content-muted)">
              Mostrando as primeiras {VISIBLE_LINES} de {plural(matching.length, 'linha', 'linhas')}{' '}
              deste filtro.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

// ── O que falta ─────────────────────────────────────────────────────────────────────

function Cadastro({
  preview,
  options,
  onOptions,
  canCreateJobRoles,
}: LaborConferenciaProps & { readonly preview: LaborImportPreview }) {
  const { missing } = preview;

  if (missing.jobRoles.length === 0 && missing.items === 0) {
    return (
      <p className="text-sm text-(--color-content-muted)">
        O cadastro já tem as funções que a planilha cita, e o cronograma por mão de obra já tem os
        itens.
      </p>
    );
  }

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3">
      <legend className="px-1 text-sm font-medium">O que falta</legend>

      {missing.jobRoles.length > 0 && (
        <Option
          id="opt-labor-roles"
          checked={options.createMissingJobRoles}
          disabled={!canCreateJobRoles}
          onChange={(checked) => onOptions({ ...options, createMissingJobRoles: checked })}
          label={`Criar as funções que faltam (${String(missing.jobRoles.length)})`}
        >
          {canCreateJobRoles
            ? `${listed(missing.jobRoles)} — entram no cadastro de funções, que vale para todas as obras. Sem marcar, as linhas dessas funções ficam com erro. Os serviços de terceiros não entram.`
            : 'Criar função é do cadastro da mão de obra, e o seu perfil não o administra.'}
        </Option>
      )}

      {missing.items > 0 && (
        <p role="note" className="text-xs text-(--color-state-warn)">
          {plural(missing.items, 'item', 'itens')} da planilha não{' '}
          {missing.items === 1 ? 'existe' : 'existem'} no cronograma por mão de obra desta obra. As
          linhas {missing.items === 1 ? 'dele ficam' : 'deles ficam'} com erro até o cronograma por
          mão de obra ser importado.
        </p>
      )}
    </fieldset>
  );
}
